import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import tngImage from './assets/payment/tng.png';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getCart, getCartTotal, getCartTotalWeight, clearCart, addToCart, removeFromCart } from './services/cartService';
import { uploadPaymentReceipt, getShippingFees, getProducts } from './services/firebaseService';

// Import state flag images
import johorFlag from './assets/states/johor.png';
import kedahFlag from './assets/states/kedah.png';
import kelantanFlag from './assets/states/kelantan.png';
import malaccaFlag from './assets/states/malacca.png';
import negeriSembilanFlag from './assets/states/negeriSembilan.png';
import pahangFlag from './assets/states/pahang.png';
import penangFlag from './assets/states/penang.png';
import perakFlag from './assets/states/perak.png';
import perlisFlag from './assets/states/perlis.png';
import sabahFlag from './assets/states/sabah.png';
import sarawakFlag from './assets/states/sarawak.png';
import selangorFlag from './assets/states/selangor.png';
import terengganuFlag from './assets/states/terengganu.png';
import klFlag from './assets/states/kl.png';
import labuanFlag from './assets/states/labuan.png';
import putrajayaFlag from './assets/states/putrajaya.png';

// State mapping with flags
const malaysianStates = [
  { name: 'Johor', flag: johorFlag },
  { name: 'Kedah', flag: kedahFlag },
  { name: 'Kelantan', flag: kelantanFlag },
  { name: 'Melaka', flag: malaccaFlag },
  { name: 'Negeri Sembilan', flag: negeriSembilanFlag },
  { name: 'Pahang', flag: pahangFlag },
  { name: 'Penang', flag: penangFlag },
  { name: 'Perak', flag: perakFlag },
  { name: 'Perlis', flag: perlisFlag },
  { name: 'Sabah', flag: sabahFlag },
  { name: 'Sarawak', flag: sarawakFlag },
  { name: 'Selangor', flag: selangorFlag },
  { name: 'Terengganu', flag: terengganuFlag },
  { name: 'WP Kuala Lumpur', flag: klFlag },
  { name: 'WP Labuan', flag: labuanFlag },
  { name: 'WP Putrajaya', flag: putrajayaFlag },
];

const CheckOut = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    contactNumber: '',
    addressLine1: '',
    addressLine2: '',
    cityDistrict: '',
    postcode: '',
    state: '',
    quantity: 1,
    paymentMethod: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [shippingFeesLoading, setShippingFeesLoading] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [errors, setErrors] = useState({});
  const [orderId, setOrderId] = useState('');
  const [agreements, setAgreements] = useState({
    termsAndConditions: false,
    privacyPolicy: false,
    warrantyPolicy: false
  });
  const [shippingFeesConfig, setShippingFeesConfig] = useState({
    westMalaysia: { firstKg: 10, additionalKg: 3 },
    eastMalaysia: { firstKg: 10, additionalKg: 12 }
  });
  const isFromCart = !id; // If no ID, it's from cart

  // Generate custom order ID
  const generateOrderId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const randomNum = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
    return `${year}${month}${randomNum}`;
  };

  // Generate orderId when component mounts
  useEffect(() => {
    setOrderId(generateOrderId());
  }, []);

  useEffect(() => {
    const loadShippingFees = async () => {
      const result = await getShippingFees();
      if (result.success) {
        setShippingFeesConfig(result.shippingFees);
      }
    };

    loadShippingFees();
  }, []);

  const openShippingPolicyModal = async () => {
    setShippingFeesLoading(true);
    const result = await getShippingFees();
    if (result.success) {
      setShippingFeesConfig(result.shippingFees);
    }
    setShippingFeesLoading(false);
    setShowShippingModal(true);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError('');
      
      try {
        if (isFromCart) {
          // Load cart items for cart checkout
          const cart = getCart();
          if (cart.length === 0) {
            setError('Your cart is empty');
            setLoading(false);
            return;
          }
          setCartItems(cart);
        } else {
          // Load single product for direct checkout - add to cart for consistent handling
          const docRef = doc(db, 'products', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const productData = { id: docSnap.id, ...docSnap.data() };
            setProduct(productData);
            
            // Get quantity from URL parameter
            const queryParams = new URLSearchParams(location.search);
            const urlQuantity = parseInt(queryParams.get('quantity')) || 1;
            
            // Remove existing item with same SKU to avoid quantity accumulation
            removeFromCart(productData.sku);
            
            // Add product to cart for consistent checkout handling
            const cartItem = {
              title: productData.name,
              sku: productData.sku,
              quantity: urlQuantity,
              price: productData.price,
              weight: productData.weight || 0,
              image: productData.image
            };
            // Add to cart temporarily for checkout
            addToCart(cartItem);
            // Get all cart items for display
            const cart = getCart();
            setCartItems(cart);
          } else {
            setError('Product not found.');
          }
        }
      } catch (err) {
        setError('Failed to load data.');
      }
      setLoading(false);
    };
    fetchProduct();
  }, [id, isFromCart, location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate postcode
    if (name === 'postcode') {
      // Only allow numeric values
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
      
      // Set validation error
      if (numericValue && numericValue.length !== 5) {
        setErrors(prev => ({
          ...prev,
          postcode: 'Postcode must be exactly 5 digits'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.postcode;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate payment receipt is uploaded
    if (!paymentReceipt) {
      alert('Please upload your payment receipt before placing the order.');
      return;
    }
    
    // Validate postcode
    if (formData.postcode.length !== 5) {
      alert('Postcode must be exactly 5 digits');
      return;
    }
    
    // Validate state is selected
    if (!formData.state) {
      setErrors(prev => ({ ...prev, state: 'Please select a state' }));
      setShowStateModal(true);
      alert('Please select a delivery state');
      return;
    }
    
    // Validate all agreements are accepted
    if (!agreements.termsAndConditions || !agreements.privacyPolicy || !agreements.warrantyPolicy) {
      setErrors(prev => ({ 
        ...prev, 
        agreements: t('checkout.agreementsRequired') 
      }));
      alert(t('checkout.agreementsRequired'));
      return;
    }

    // Strip out products the admin has disabled or deleted since they were added
    const productsResult = await getProducts();
    if (productsResult.success) {
      const productsBySku = {};
      productsResult.products.forEach((p) => {
        if (p.sku) productsBySku[p.sku] = p;
      });
      const unavailable = cartItems.filter((item) => {
        const match = productsBySku[item.sku];
        return !match || match.status === 'disable';
      });
      if (unavailable.length > 0) {
        unavailable.forEach((item) => removeFromCart(item.sku));
        setCartItems(getCart());
        const names = unavailable.map((item) => item.title || item.name || item.sku).join(', ');
        alert(t('checkout.productUnavailable', { products: names }));
        return;
      }
    }

    setSubmitting(true);
    
    try {
      // Use pre-generated order ID
      if (!orderId) {
        alert('Order ID not generated. Please refresh the page.');
        setSubmitting(false);
        return;
      }
      
      // Upload payment receipt (required)
      const receiptUrl = await uploadPaymentReceipt(paymentReceipt, orderId);
      
      // Prepare products array
      const products = cartItems.map(item => {
        const itemPrice = parseFloat(item.price) || 0;
        return {
          sku: item.sku || '',
          productName: item.title || item.name || '',
          quantity: item.quantity,
          amount: itemPrice,
          totalAmount: itemPrice * item.quantity
        };
      });
      
      // Prepare address as an object
      const purchaserAddress = {
        line1: formData.addressLine1,
        line2: formData.addressLine2,
        city: formData.cityDistrict,
        postcode: formData.postcode,
        state: formData.state
      };
      
      // Calculate amounts
      const amount = getCartTotal(); // Amount before shipping
      const shippingFees = calculateShippingFee();
      const totalWeight = getCartTotalWeight();
      const totalAmount = amount + shippingFees; // Amount with shipping
      
      // Prepare order data according to your Firebase structure
      const orderData = {
        orderId: orderId,
        purchaserName: formData.name,
        purchaserPhoneNumber: formData.contactNumber,
        purchaserAddress: purchaserAddress,
        dateTime: new Date(),
        amount: amount,
        shippingFees: shippingFees,
        totalWeight: totalWeight,
        totalAmount: totalAmount,
        status: 'processing',
        products: products,
        paymentReceipt: paymentReceipt.name,
        receiptUrl: receiptUrl
      };
      
      // Save order to Firebase with custom ID
      await setDoc(doc(db, 'orders', orderId), orderData);
      
      // Clear cart after successful order
      clearCart();
      
      // Navigate to order success page with order ID
      navigate('/order-success', { state: { orderId } });
    } catch (err) {
      console.error('Error placing order:', err);
      alert(t('checkout.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    // Convert to string if it's a number
    const priceStr = typeof price === 'number' ? price.toString() : price;
    const cleanPrice = priceStr.replace(/^RM\s*/, '').replace(/[^\d.]/g, '');
    const numPrice = parseFloat(cleanPrice);
    if (isNaN(numPrice)) return price;
    return `RM ${numPrice.toFixed(2)}`;
  };

  // Helper function to check if state is in East Malaysia
  const isEastMalaysia = (state) => {
    const eastMalaysiaStates = ['Sabah', 'Sarawak', 'Labuan', 'WP Labuan'];
    return eastMalaysiaStates.includes(state);
  };

  // Calculate shipping fee based on total price, weight, and region
  const calculateShippingFee = () => {
    const totalWeight = getCartTotalWeight();
    const isEast = formData.state ? isEastMalaysia(formData.state) : false; // Default to West Malaysia if no state
    
    if (isEast) {
      // East Malaysia rates
      if (totalWeight <= 1) {
        return Number(shippingFeesConfig.eastMalaysia.firstKg);
      } else {
        return Number(shippingFeesConfig.eastMalaysia.firstKg) + ((Math.ceil(totalWeight) - 1) * Number(shippingFeesConfig.eastMalaysia.additionalKg));
      }
    } else {
      // West Malaysia rates (default)
      if (totalWeight <= 1) {
        return Number(shippingFeesConfig.westMalaysia.firstKg);
      } else {
        return Number(shippingFeesConfig.westMalaysia.firstKg) + ((Math.ceil(totalWeight) - 1) * Number(shippingFeesConfig.westMalaysia.additionalKg));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-gray-500 text-xl">{t('checkout.loading')}</div>
      </div>
    );
  }

  // Check for errors or empty cart
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-500 text-xl">{error}</div>
      </div>
    );
  }
  
  // If not from cart and no product found
  if (!isFromCart && !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-500 text-xl">{t('checkout.productNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <main className="flex-grow w-full max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary mb-1">{t('checkout.title')}</h1>
            <p className="text-gray-600">Complete your order securely.</p>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-primary font-semibold hover:underline mb-6"
          >
            <ArrowBackIcon className="mr-2 !w-5 !h-5" />
            {t('checkout.backToProduct')}
          </button>

          {orderId && (
            <div className="mb-6 text-sm bg-blue-100 text-primary px-4 py-3 rounded border border-blue-200">
              <span className="font-semibold">Order ID: </span>
              <span className="font-mono text-lg">{orderId}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Left Column: Shipping Information */}
            <div className="md:col-span-7 lg:col-span-8 flex flex-col gap-6">
              <section className="bg-white border border-gray-200 rounded p-6">
                <h2 className="text-xl font-bold text-primary mb-4 pb-2 border-b border-gray-200">
                  Shipping Information
                </h2>
                <form id="checkout-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        <PersonIcon className="inline !w-4 !h-4 mr-1" />
                        {t('checkout.name')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder={t('checkout.namePlaceholder')}
                      />
                    </div>

                    <div>
                      <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        <PhoneIcon className="inline !w-4 !h-4 mr-1" />
                        {t('checkout.contactNumber')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="contactNumber"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder={t('checkout.contactPlaceholder')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <LocationOnIcon className="inline !w-4 !h-4 mr-1" />
                        Delivery Address <span className="text-red-500">*</span>
                      </label>
<div className="mb-3">
                        <label htmlFor="addressLine1" className="block text-xs text-gray-600 mb-1">Line 1</label>
                        <input
                          type="text"
                          id="addressLine1"
                          name="addressLine1"
                          value={formData.addressLine1}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                          placeholder="Street address or house number"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label htmlFor="addressLine2" className="block text-xs text-gray-600 mb-1">Line 2</label>
                        <input
                          type="text"
                          id="addressLine2"
                          name="addressLine2"
                          value={formData.addressLine2}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                          placeholder="Apartment, suite, unit, etc."
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label htmlFor="cityDistrict" className="block text-xs text-gray-600 mb-1">City/District</label>
                        <input
                          type="text"
                          id="cityDistrict"
                          name="cityDistrict"
                          value={formData.cityDistrict}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                          placeholder="City or District"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label htmlFor="postcode" className="block text-xs text-gray-600 mb-1">Postcode</label>
                        <input
                          type="text"
                          id="postcode"
                          name="postcode"
                          value={formData.postcode}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                          placeholder="Postcode"
                          maxLength="5"
                        />
                        {errors.postcode && (
                          <p className="text-red-500 text-xs mt-1">{errors.postcode}</p>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">State <span className="text-red-500">*</span></label>
                        <button
                          type="button"
                          onClick={() => setShowStateModal(true)}
                          className={`w-full px-3 py-2 border-2 rounded-lg text-left flex items-center justify-between transition-all ${
                            formData.state
                              ? 'border-primary bg-blue-50'
                              : 'border-gray-300 bg-white hover:border-gray-400'
                          } ${errors.state ? 'border-red-500' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            {formData.state ? (
                              <>
                                {malaysianStates.find(s => s.name === formData.state)?.flag && (
                                  <img
                                    src={malaysianStates.find(s => s.name === formData.state).flag}
                                    alt={formData.state}
                                    className="w-6 h-6 object-contain"
                                  />
                                )}
                                <span className="text-sm font-medium text-gray-900">{formData.state}</span>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">Select State</span>
                            )}
                          </div>
                          <span className="text-gray-400">▼</span>
                        </button>
                        {errors.state && (
                          <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                        )}
                        {!formData.state && !errors.state && (
                          <p className="text-gray-500 text-xs mt-1">Please select a state</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Payment Methods
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setShowPaymentModal(true)}
                            className="text-red-500 hover:text-red-700 font-medium text-sm underline"
                          >
                            Select
                          </button>
                          {paymentReceipt && (
                            <span className="text-sm text-green-600 font-medium">
                              ✓ {paymentReceipt.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* <div className="pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-primary hover:bg-blue-900 text-white font-semibold py-3 px-6 rounded-lg shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? t('checkout.processing') : t('checkout.placeOrder')}
                      </button>
                    </div> */}
                  </form>
              </section>
            </div>

            {/* Right Column: Final Order Summary */}
            <div className="md:col-span-5 lg:col-span-4">
              <aside className="bg-white border border-gray-200 rounded p-6 md:sticky md:top-24">
                <h2 className="text-xl font-bold text-primary mb-4 pb-2 border-b border-gray-200">
                  Final Order Summary
                </h2>

                {cartItems.length > 0 ? (
                  <div className="flex flex-col gap-4 mb-4 border-b border-gray-200 pb-4">
                    {cartItems.map((item) => (
                      <div key={item.sku} className="flex justify-between items-start gap-3">
                        <div className="flex gap-3">
                          <div className="w-16 h-16 bg-gray-100 flex-shrink-0 border border-gray-200 rounded flex items-center justify-center overflow-hidden">
                            {item.image ? (
                              <img src={item.image} alt={item.title} className="w-full h-full object-contain" />
                            ) : (
                              <ShoppingCartIcon className="!w-6 !h-6 text-gray-400" />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-semibold text-primary">{item.title}</span>
                            <span className="text-xs text-gray-500 mt-1">Weight: {Number(item.weight || 0).toFixed(2)} kg</span>
                            <span className="text-xs text-gray-500">Qty: {item.quantity}</span>
                          </div>
                        </div>
                        <span className="text-sm text-primary font-semibold">{formatPrice(parseFloat(item.price.toString().replace(/[^\d.]/g, '')) * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">No items to checkout</p>
                )}

                <div className="flex flex-col gap-2 mb-6 text-sm text-gray-600">
                  <div className="flex justify-between items-center">
                    <span>Subtotal</span>
                    <span className="font-semibold">{formatPrice(getCartTotal())}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Total Weight</span>
                    <span>{getCartTotalWeight().toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1">
                      Shipping Fee
                      <button
                        type="button"
                        onClick={openShippingPolicyModal}
                        className="text-primary hover:text-blue-900 transition-colors"
                        aria-label="View shipping policy"
                      >
                        <InfoOutlinedIcon className="!w-4 !h-4" />
                      </button>
                    </span>
                    <span>{calculateShippingFee() === 0 ? 'FREE' : formatPrice(calculateShippingFee())}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-1">
                    <span className="text-lg font-bold text-primary">Grand Total</span>
                    <span className="text-lg font-bold text-primary">{formatPrice(getCartTotal() + calculateShippingFee())}</span>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-200 mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('checkout.agreements')} <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 py-1">
                      <input
                        type="checkbox"
                        id="termsAndConditions"
                        checked={agreements.termsAndConditions}
                        onChange={(e) => {
                          setAgreements(prev => ({ ...prev, termsAndConditions: e.target.checked }));
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.agreements;
                            return newErrors;
                          });
                        }}
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="termsAndConditions" className="text-sm text-gray-700 cursor-pointer">
                        {t('checkout.agreeTo')}{' '}
                        <a
                          href="/assets/docs/mmk_tnc.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('footer.termsAndConditions')}
                        </a>
                      </label>
                    </div>
                    <div className="flex items-start gap-3 py-1">
                      <input
                        type="checkbox"
                        id="privacyPolicy"
                        checked={agreements.privacyPolicy}
                        onChange={(e) => {
                          setAgreements(prev => ({ ...prev, privacyPolicy: e.target.checked }));
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.agreements;
                            return newErrors;
                          });
                        }}
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="privacyPolicy" className="text-sm text-gray-700 cursor-pointer">
                        {t('checkout.agreeTo')}{' '}
                        <a
                          href="/assets/docs/mmk_privacy.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('footer.privacyPolicy')}
                        </a>
                      </label>
                    </div>
                    <div className="flex items-start gap-3 py-1">
                      <input
                        type="checkbox"
                        id="warrantyPolicy"
                        checked={agreements.warrantyPolicy}
                        onChange={(e) => {
                          setAgreements(prev => ({ ...prev, warrantyPolicy: e.target.checked }));
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.agreements;
                            return newErrors;
                          });
                        }}
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="warrantyPolicy" className="text-sm text-gray-700 cursor-pointer">
                        {t('checkout.agreeTo')}{' '}
                        <a
                          href="/assets/docs/mmk_warranty.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('footer.warrantyPolicy')}
                        </a>
                      </label>
                    </div>
                  </div>
                  {errors.agreements && (
                    <p className="text-red-500 text-xs mt-2">{errors.agreements}</p>
                  )}
                </div>

                <button
                  type="submit"
                  form="checkout-form"
                  disabled={submitting}
                  className="w-full bg-primary text-white hover:bg-blue-900 transition-colors py-3 px-4 rounded font-bold flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? t('checkout.processing') : t('checkout.placeOrder')}
                </button>
                
              </aside>
            </div>
          </div>
        </main>

      {/* Shipping Policy Modal */}
      {showShippingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowShippingModal(false)}>
          <div className="bg-white rounded-2xl p-4 md:p-8 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">{t('shipping.title')}</h2>
              <button
                onClick={() => setShowShippingModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {shippingFeesLoading && (
                <p className="text-gray-500 text-sm">Refreshing latest shipping fees...</p>
              )}
              <div>
                <h3 className="text-lg font-semibold text-primary mb-3">West Malaysia</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">Standard Delivery</h4>
                  <p className="text-blue-700 text-sm">First 1kg: RM{Number(shippingFeesConfig.westMalaysia.firstKg).toFixed(2)}</p>
                  <p className="text-blue-700 text-sm">Additional 1kg: RM{Number(shippingFeesConfig.westMalaysia.additionalKg).toFixed(2)}</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-primary mb-3">East Malaysia (Sabah, Sarawak, Labuan)</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">Standard Delivery</h4>
                  <p className="text-blue-700 text-sm">First 1kg: RM{Number(shippingFeesConfig.eastMalaysia.firstKg).toFixed(2)}</p>
                  <p className="text-blue-700 text-sm">Additional 1kg: RM{Number(shippingFeesConfig.eastMalaysia.additionalKg).toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowShippingModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-semibold"
              >
                {t('shipping.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* State Selection Modal */}
      {showStateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowStateModal(false)}>
          <div className="bg-white rounded-2xl p-4 md:p-8 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">Select Delivery State</h2>
              <button 
                onClick={() => setShowStateModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {malaysianStates.map((state) => (
                <button
                  key={state.name}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, state: state.name }));
                    setErrors(prev => ({ ...prev, state: '' }));
                    setShowStateModal(false);
                  }}
                  className={`flex flex-col items-center p-4 border-2 rounded-lg transition-all hover:shadow-md ${
                    formData.state === state.name
                      ? 'border-primary bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  {state.flag ? (
                    <img
                      src={state.flag}
                      alt={state.name}
                      className="w-16 h-16 object-contain mb-2"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded mb-2 flex items-center justify-center">
                      <span className="text-xs text-gray-500">N/A</span>
                    </div>
                  )}
                  <span className={`text-xs text-center ${
                    formData.state === state.name ? 'font-semibold text-primary' : 'text-gray-700'
                  }`}>
                    {state.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Payment Methods Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl p-4 md:p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">Payment Methods</h2>
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>
            
            {/* Payment Steps */}
            <div className="bg-blue-50 border-l-4 border-primary p-4 mb-6 rounded">
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                <li>Choose your preferred payment method</li>
                <li>Upload your payment slip at below</li>
              </ol>
            </div>
            
            <div className="space-y-6">
              {/* DuitNow Payment Option */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-primary mb-4">DuitNow Payment</h3>
                <div className="mb-4">
                  <img src={tngImage} alt="TNG eWallet" className="mx-auto w-full max-w-[240px] sm:max-w-xs md:max-w-sm object-contain" />
                </div>
                <p className="text-sm text-gray-600 text-center">Scan using your preferred e-wallet or online banking app</p>
              </div>

              {/* OR Divider */}
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-4 text-gray-500 font-medium">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Online Banking Option */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-primary mb-4">Online Banking</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-2">Transfer to following bank account:</p>
                  <div className="space-y-1 text-sm mb-4">
                    <p className="font-semibold text-gray-800">MMK INDUSTRIES SDN BHD</p>
                    <p className="text-gray-700">PUBLIC BANK BERHAD</p>
                    <p className="text-gray-700 font-mono">3815870235</p>
                  </div>
                  {orderId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <p className="text-sm font-semibold text-blue-800 mb-1">Important:</p>
                      <p className="text-sm text-blue-700">
                        Please use <span className="font-mono font-bold">{orderId}</span> as your transfer reference when making the payment.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Payment Receipt - Common for both */}
              <div className="border border-gray-200 rounded-lg p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Payment Receipt
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPaymentReceipt(e.target.files[0])}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {paymentReceipt && (
                  <p className="text-sm text-green-600 mt-2">File selected: {paymentReceipt.name}</p>
                )}
              </div>

              {/* Confirm Button */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!paymentReceipt) {
                      alert('Please upload payment receipt');
                      return;
                    }
                    setShowPaymentModal(false);
                  }}
                  className="flex-1 bg-primary hover:bg-blue-900 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckOut;
